// SCR-MAP (D1–D3), MOD-BOSS (одиночный и «Кооператив»), MOD-BOSS-LOCKED — E04-T06, E04-T07, ux/04 §1–3.
// Лента строится из GET /districts: уровень открытия, «×N побед», «Зачистка» — поля договора (minLevel, winsSolo,
// sweep); если сервер их не прислал — уровень из clientConfig.values.bosses или ГД 06 §3 (решение Р-36).
// Офлайн — карта из кэша с подписью времени, кнопка боя в MOD-BOSS неактивна.
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { COMBAT_TIMING } from '@garages/contract';
import { useApp } from '../app/app.js';
import { bossInfo, DISTRICT_LEVELS, DISTRICT_NAMES } from '../content/catalog.js';
import { PowerBlock } from './power.js';
import { CoopBossBody, CoopHp, CoopStrip, useCoopFight, type CoopInst } from './instances.js';
import { ApiCallError } from '../net/api-client.js';
import { clientConfig, districts, online, player, refreshProfile, saveCache, wallet, type Districts } from '../state/game.js';
import { storage } from '../state/storage.js';
import { amount, num } from '../i18n/format.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { stage } from '../ui/App.js';
import { ErrorBanner, ResourceHeader, Sheet, Skeleton } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { LAST_BATTLE_KEY } from '../boot/boot.js';
import { I, ICONS } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { assetEntry, assetUrl, bgId } from '../assets/manifest.js';
import { viewport } from '../telegram/viewport.js';
import { t as tr } from '../i18n/t.js';

export function bossState(b: { bossId: string; unlocked: boolean; minLevel?: number; winsSolo?: number; sweep?: boolean }, myLevel: number) {
  const base = bossInfo(b.bossId, clientConfig.value);
  const info = b.minLevel ? { ...base, level: b.minLevel } : base;
  return { info, locked: !b.unlocked, wins: b.winsSolo ?? 0, sweep: !!b.sweep, tooStrong: b.unlocked && info.level >= myLevel + 3 };
}

/** Площадки под метки боссов на арте BG-MAP-D1 (390 × 844, снизу вверх по дорожке района), доли ширины и высоты. */
export const D1_PADS: Record<string, [number, number]> = { B01: [0.633, 0.681], B02: [0.238, 0.509], B03: [0.756, 0.509], B04: [0.751, 0.273] };

/** Точка арта (cover, прижат к низу) → координаты в контейнере. */
export function padToScreen(pad: [number, number], box: { w: number; h: number }, img: { w: number; h: number } = { w: 390, h: 844 }): [number, number] {
  const k = Math.max(box.w / img.w, box.h / img.h);
  const offX = (box.w - img.w * k) / 2;
  const offY = box.h - img.h * k;
  return [offX + pad[0] * img.w * k, offY + pad[1] * img.h * k];
}

type MapBoss = { bossId: string; unlocked: boolean; minLevel?: number; winsSolo?: number; sweep?: boolean; coopInstance?: CoopInst; coopResult?: { iid: string } };

/** Тап по точке: закрыт — MOD-BOSS-LOCKED; подарок — итог инстанса; вызван — MOD-BOSS «Кооператив»; иначе MOD-BOSS. */
function openBoss(app: ReturnType<typeof useApp>, b: MapBoss, myLevel: number, gift: boolean) {
  const st = bossState(b, myLevel);
  app.haptic('selection');
  if (st.locked) app.nav.openSheet('MOD-BOSS-LOCKED', { bossId: b.bossId, level: st.info.level, wins: st.wins });
  else if (gift) app.nav.openSheet('MOD-INSTANCE-RESULT', { bossId: b.bossId, ...(b.coopResult ? { iid: b.coopResult.iid } : {}) });
  else app.nav.openSheet('MOD-BOSS', { bossId: b.bossId, level: st.info.level, wins: st.wins, ...(b.coopInstance ? { mode: 'coop' } : {}) });
}

/** Точка босса 72 pt (ux/04 §1.2): портрет, «×N», замок, метла, треугольник; вызванный инстанс — рамка, HP, срок; подарок. */
function BossDot(p: { b: MapBoss; myLevel: number; gift: boolean; style?: { left: number; top: number } }) {
  const app = useApp();
  const st = bossState(p.b, p.myLevel);
  const coop = p.b.coopInstance;
  return (
    <button class={`boss-token ${st.locked ? 'locked' : ''} ${coop ? 'coop' : ''} ${!st.locked && (!st.wins || coop) ? 'fresh' : ''}`} style={p.style} data-testid={`boss-${p.b.bossId}`}
      data-state={st.locked ? 'locked' : coop ? 'coop' : p.gift ? 'gift' : 'open'}
      aria-label={`${st.info.name}${st.locked ? tr('CL-MAP-TOKEN-LOCKED', { n: st.info.level }) : ''}`}
      onClick={() => openBoss(app, p.b, p.myLevel, p.gift)}>
      <span class="boss-portrait"><Icon id={`ICO-BOSS-${p.b.bossId}`} fallback={ICONS.boss[1]} size={60} /></span>
      {st.locked ? <span class="token-lock"><I n="lock" size={16} /></span> : null}
      {st.wins ? <span class="token-wins">×{st.wins}</span> : null}
      {st.sweep ? <span class="token-sweep"><I n="sweep" size={16} /></span> : null}
      {st.tooStrong ? <span class="token-warn"><I n="warn" size={16} /></span> : null}
      {p.gift ? <span class="token-gift" data-testid={`gift-${p.b.bossId}`}><I n="party" size={20} /></span> : null}
      <span class="token-name">{st.locked ? tr('CL-MAP-TOKEN-LVL', { n: st.info.level }) : st.info.short}</span>
      {coop ? <CoopHp inst={coop} compact /> : null}
    </button>
  );
}

/** Жетоны боссов поверх арта района (замечание владельца: сцена видна, меню компактные, как здания на карте). */
function MapTokens(p: { bosses: MapBoss[]; myLevel: number; gifts: Set<string> }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 390, h: 844 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const upd = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div class="map-tokens" ref={ref}>
      {p.bosses.map((b) => {
        const pad = D1_PADS[b.bossId];
        if (!pad) return null;
        const [x, y] = padToScreen(pad, box);
        return <BossDot key={b.bossId} b={b} myLevel={p.myLevel} gift={p.gifts.has(b.bossId)} style={{ left: x, top: y }} />;
      })}
    </div>
  );
}

/** Арт района под его секцией ленты (BG-MAP-D1…D3, на ≥ 1024 — -XL): районы стыкуются снизу вверх. */
function districtArt(id: string): { backgroundImage?: string } {
  const url = assetUrl(bgId(`BG-MAP-${id}`, viewport.value.w));
  return url ? { backgroundImage: `url("${url}")` } : {};
}

export function MapScreen(_p: ScreenProps) {
  const app = useApp();
  useEffect(() => { void stage.value?.show('none'); }, [stage.value]);
  const load = useLoad(() => app.call('getDistricts'), {
    cached: districts.value?.data ?? null,
    onData: (d) => { districts.value = { data: d, at: Date.now() }; saveCache(); },
  });
  const myLevel = player.value?.level ?? 1;
  const at = districts.value?.at;
  const data = load.data;
  // Подарок на точке — законченный инстанс с неоткрытым итогом (`coopResult`, Р-89; только участнику с вкладом).
  const gifts = new Set((data?.districts ?? []).flatMap((d) => d.bosses.filter((b) => b.coopResult?.unseen).map((b) => b.bossId)));
  const open = data?.districts.filter((d) => d.open) ?? [];
  // Только D1 открыт и есть его арт на обычной высоте — жетоны поверх арта (сцена видна целиком, E04-T06).
  // Дальше — лента D3…D1 снизу вверх, точки боссов рядами по 4 (ux/04 §1.1); фон района виден между рядами.
  const tokens = viewport.value.height === 'normal' && !!data && open.length === 1 && open[0]!.districtId === 'D1'
    && bgId('BG-MAP-D1', viewport.value.w) === 'BG-MAP-D1' && !!assetEntry('BG-MAP-D1');
  const top = open.at(-1)?.districtId;
  // Лента длиннее экрана: показываем старший открытый район (ux/04 §1.1 — вертикальная лента снизу вверх).
  useLayoutEffect(() => { if (top) document.querySelector(`[data-testid="district-${top}"]`)?.scrollIntoView({ block: 'end' }); }, [top, !!data]);
  if (tokens) {
    const locked = data!.districts.filter((d) => !d.open);
    return (
      <>
        <ResourceHeader />
        <div class="content transparent" data-testid="SCR-MAP" data-view="tokens">
          {load.offline && at ? <div class="map-note">{tr('CL-MAP-OFFLINE', { p0: new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) })}</div> : null}
          <CoopStrip data={data} />
          {locked.map((d) => (
            <div key={d.districtId} class="map-note" data-testid={`district-${d.districtId}`}>
              <I n="lock" size={14} /> {d.districtId}: {d.lock?.bossId ? tr('CL-MAP-LOCK-BOSS', { boss: bossInfo(d.lock.bossId).short }) : ''}{d.lock?.level ? ` · ${tr('CL-MAP-LOCK-LEVEL', { n: d.lock.level })}` : ''}{!d.lock?.bossId && !d.lock?.level ? tr('CL-MAP-FOG') : ''}
            </div>
          ))}
        </div>
        <MapTokens bosses={open[0]!.bosses} myLevel={myLevel} gifts={gifts} />
      </>
    );
  }
  const ribbon = [...(data?.districts ?? [])].reverse();
  return (
    <>
      <ResourceHeader />
      <div class="content screen-bg map-ribbon" data-testid="SCR-MAP" data-view="ribbon">
        <div class="page col map-page">
          {load.error && !load.offline ? <ErrorBanner what={tr('CL-SCREENS-MAP-01')} onRetry={load.reload} /> : null}
          {load.offline && at ? <div class="banner small">{tr('CL-SCREENS-MAP-02')}{" "}{new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} {" "}{tr('CL-SCREENS-MAP-03')}</div> : null}
          <CoopStrip data={data} />
          {!data ? (load.loading ? <Skeleton h={120} n={3} /> : <div class="empty"><I n="map" size={48} class="ill" />{tr('CL-SCREENS-MAP-04')}<button class="btn small outline" onClick={load.reload}>{tr('CL-SCREENS-MAP-05')}</button></div>) : null}
          <div class="map-fog small" data-testid="map-fog">{tr('CL-MAP-FOG')}</div>
          {ribbon.map((d) => d.open ? (
            <section key={d.districtId} class="district art" aria-label={d.districtId} data-testid={`district-${d.districtId}`} data-open="1" style={districtArt(d.districtId)}>
              <h2 class="district-title">{d.districtId} · {DISTRICT_NAMES[d.districtId] ?? tr('CL-SCREENS-MAP-09')}{DISTRICT_LEVELS[d.districtId] ? <span class="small"> · {tr('CL-MAP-LVLS', { r: DISTRICT_LEVELS[d.districtId] })}</span> : null}</h2>
              <div class="district-dots">
                {d.bosses.map((b) => <BossDot key={b.bossId} b={b} myLevel={myLevel} gift={gifts.has(b.bossId)} />)}
              </div>
            </section>
          ) : (
            <section key={d.districtId} class="district locked art" data-testid={`district-${d.districtId}`} data-open="0" style={districtArt(d.districtId)}>
              <h2 class="district-title"><I n="fog" size={20} /> {d.districtId} · {DISTRICT_NAMES[d.districtId] ?? tr('CL-SCREENS-MAP-06')}</h2>
              <span class="small">{d.lock?.bossId ? tr('CL-SCREENS-MAP-07', { p0: bossInfo(d.lock.bossId).short }) : ''}{d.lock?.bossId && d.lock.level ? ' · ' : ''}{d.lock?.level ? tr('CL-SCREENS-MAP-08', { p0: d.lock.level }) : ''}</span>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Босс для «В бой» на главном (ux/03 §1.2): последний, с кем дрался, если он открыт; иначе рекомендованный —
 * самый сильный открытый в кэше карты; иначе B01.
 */
export function pickHomeBoss(d: Districts | null, last: string | null): { bossId: string; level?: number; wins?: number } {
  const bosses = (d?.districts ?? []).filter((x) => x.open).flatMap((x) => x.bosses);
  const open = bosses.filter((b) => b.unlocked);
  const chosen = (last && open.find((b) => b.bossId === last)) || open.at(-1);
  if (!chosen) return { bossId: 'B01' };
  return { bossId: chosen.bossId, ...(chosen.minLevel ? { level: chosen.minLevel } : {}), ...(chosen.winsSolo !== undefined ? { wins: chosen.winsSolo } : {}) };
}

const withLevel = <T extends { level: number }>(info: T, level: unknown): T => (typeof level === 'number' && level > 0 ? { ...info, level } : info);

/** «В бой» → POST /battles с одним ключом на все повторы; > 3 с — «Соединяемся…», > 10 с — тост (ux/04 §2.3). */
export function BossSheet({ entry }: SheetProps) {
  if (entry.params['mode'] === 'coop') return <CoopBossSheet entry={entry} />;
  return <SoloBossSheet entry={entry} />;
}

/** MOD-BOSS в режиме «Кооператив» (ux/04 §2): общий инстанс, «В бой» тратит 10 ⚡, итог — MOD-INSTANCE-RESULT. */
function CoopBossSheet({ entry }: SheetProps) {
  const app = useApp();
  const bossId = String(entry.params['bossId']);
  const info = bossInfo(bossId, clientConfig.value);
  const fight = useCoopFight(bossId);
  const cost = COMBAT_TIMING.entryEnergy;
  const enough = (wallet.value?.energy.value ?? 0) >= cost;
  return (
    <Sheet entry={entry} title={info.name} actions={[
      { text: tr('CL-INST-SOLO'), tone: 'outline', testId: 'boss-solo', onClick: () => app.nav.openSheet('MOD-BOSS', { bossId }, { replace: true }) },
      !online.value ? { text: tr('UI-OFFLINE'), testId: 'boss-go', disabled: true, onClick: () => {} }
        : !enough ? { text: tr('CL-SCREENS-MAP-25', { p0: cost }), icon: 'energy' as const, testId: 'boss-go', onClick: () => app.nav.openSheet('MOD-ENERGY', { need: cost }) }
          : { text: tr('CL-INST-GO', { n: cost }), icon: 'energy' as const, testId: 'boss-go', onClick: fight },
    ]}>
      <CoopBossBody bossId={bossId} onResult={(inst) => app.nav.openSheet('MOD-INSTANCE-RESULT', { bossId, instance: inst }, { replace: true })} />
    </Sheet>
  );
}

function SoloBossSheet({ entry }: SheetProps) {
  const app = useApp();
  const bossId = String(entry.params['bossId']);
  const info = withLevel(bossInfo(bossId, clientConfig.value), entry.params['level']);
  const wins = Number(entry.params['wins'] ?? 0);
  const w = wallet.value;
  const me = player.value;
  const key = useRef(app.idem());
  const [connecting, setConnecting] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [fullTank, setFullTank] = useState(false);
  // Перед боем — свежий профиль: энергия, уровень и шаг обучения с сервера, а не из кэша (§6 п. 1).
  useEffect(() => { void refreshProfile((n) => app.call(n)); }, []);
  const cost = COMBAT_TIMING.entryEnergy;
  const tankAvailable = (me?.level ?? 0) >= 5;
  const enoughEnergy = (w?.energy.value ?? 0) >= cost;
  const go = async () => {
    if (activeId) {
      const st = await app.call('getActiveBattle');
      app.nav.closeSheets();
      if (st) app.nav.push('SCR-FIGHT', { state: st, restored: true });
      return;
    }
    if (!enoughEnergy) { app.nav.openSheet('MOD-ENERGY', { need: cost }); return; }
    app.haptic('heavy');
    const t = setTimeout(() => setConnecting(true), 3000);
    try {
      const start = await app.call('startBattle', { body: { bossId, mode: 'solo', ...(fullTank ? { fullTank: true } : {}) }, idempotencyKey: key.current });
      storage.setJson(LAST_BATTLE_KEY, { battleId: start.battleId, seen: false, origin: bossId });
      if (w) wallet.value = { ...w, energy: { ...w.energy, value: start.energy.value, nextAt: start.energy.nextAt } };
      app.nav.closeSheets();
      app.nav.push('SCR-FIGHT', { start });
    } catch (e) {
      if (e instanceof ApiCallError) {
        if (e.code === 'BATTLE_ALREADY_ACTIVE') { setActiveId(String(e.details?.['battleId'] ?? '')); showToast(tr('CL-SCREENS-MAP-19')); return; }
        if (e.code === 'NOT_ENOUGH_DIESEL') { setFullTank(false); showToast(tr('CL-SCREENS-MAP-20'), 'error'); return; }
        if (e.code === 'LEVEL_TOO_LOW' || e.code === 'BOSS_LOCKED') { app.nav.openSheet('MOD-BOSS-LOCKED', { bossId }, { replace: true }); return; }
        if (e.code === 'NOT_ENOUGH_ENERGY') { app.nav.openSheet('MOD-ENERGY', { need: cost, details: e.details }); return; }
        if (e.code === 'NETWORK' || e.code === 'TIMEOUT' || e.code === 'INTERNAL') { showToast(tr('CL-SCREENS-MAP-21'), 'error', { text: tr('CL-SCREENS-MAP-05'), onClick: () => { void go(); } }); return; }
      }
      app.handleError(e, { resource: tr('CL-SCREENS-MAP-22') });
    } finally {
      clearTimeout(t);
      setConnecting(false);
    }
  };
  const main = !online.value
    ? { text: tr('UI-OFFLINE'), testId: 'boss-go', disabled: true, onClick: () => {} }
    : activeId
      ? { text: tr('CL-SCREENS-MAP-23'), testId: 'boss-go', onClick: go }
      : connecting ? { text: tr('CL-SCREENS-MAP-24'), testId: 'boss-go', onClick: go } : { text: tr('CL-SCREENS-MAP-25', { p0: cost }), icon: 'energy' as const, testId: 'boss-go', onClick: go };
  return (
    <Sheet entry={entry} title={info.name} actions={[main]}>
      <div class="col">
        <div class="row">
          <div style={{ width: 96, height: 96, borderRadius: 16, background: `#${info.color.toString(16).padStart(6, '0')}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, overflow: 'hidden' }} aria-hidden><Icon id={`FIG-${bossId.replace(/-T$/, '')}`} fallback={ICONS.boss[1]} size={96} /></div>
          <div class="col" style={{ gap: 4 }}>
            <span class="muted small">{DISTRICT_NAMES[info.district]} {" "}{tr('CL-SCREENS-MAP-26')}{" "}{info.level}</span>
            <span>{info.hpLabel} {" "}{tr('CL-SCREENS-MAP-27')}</span>
            {wins ? <span class="small">{tr('CL-SCREENS-MAP-28')}{" "}{wins}</span> : null}
            <span class="small muted">{tr('CL-SCREENS-MAP-29')}{" "}{cost} <I n="energy" size={14} /> {" "}{tr('CL-SCREENS-MAP-30')}{" "}{w ? `${w.energy.value}/${w.energy.max}` : '—'}</span>
          </div>
        </div>
        {me && info.level >= me.level + 3 ? <div class="banner warn">{tr('CL-SCREENS-MAP-31')}{" "}{info.level - me.level} {" "}{tr('CL-SCREENS-MAP-32')}</div> : null}
        <PowerBlock bossId={bossId} />
        {bossId === 'B04' ? <div class="banner small"><I n="warn" size={16} /> {" "}{tr('CL-SCREENS-MAP-33')}</div> : null}
        {tankAvailable ? (
          <label class="row card" style={{ justifyContent: 'space-between' }}>
            <span class="col" style={{ gap: 2 }}><span>{tr('CL-SCREENS-MAP-34')}</span><span class="small muted">{tr('CL-SCREENS-MAP-35')}{" "}{amount('diesel', w?.diesel ?? 0)}</span></span>
            <input type="checkbox" checked={fullTank} disabled={(w?.diesel ?? 0) < 3} onChange={(e) => setFullTank((e.target as HTMLInputElement).checked)} style={{ width: 28, height: 28 }} />
          </label>
        ) : null}
        {!enoughEnergy && online.value ? <div class="small neg">{tr('CL-SCREENS-MAP-36')}{" "}{cost} <I n="energy" size={14} /> {" "}{tr('CL-SCREENS-MAP-37')}</div> : null}
      </div>
    </Sheet>
  );
}

/** «Откроется на 7 уровне. Ты на 5-м: ещё N опыта» (ux/04 §3). Остаток опыта — до следующего уровня из /me. */
export function BossLockedSheet({ entry }: SheetProps) {
  const app = useApp();
  const bossId = String(entry.params['bossId']);
  const info = withLevel(bossInfo(bossId, clientConfig.value), entry.params['level']);
  const me = player.value;
  const xpLeft = me && me.xpToNext !== null && info.level === me.level + 1 ? me.xpToNext - me.xp : null;
  return (
    <Sheet entry={entry} title={info.name} actions={[{ text: tr('CL-SCREENS-MAP-38'), tone: 'outline', onClick: () => app.nav.popSheet() }]}>
      <div class="col" data-testid="boss-locked-text" style={{ alignItems: 'center', textAlign: 'center' }}>
        <div style={{ filter: 'grayscale(1) brightness(.4)' }} aria-hidden><Icon id={`FIG-${bossId}`} fallback={ICONS.boss[1]} size={96} /></div>
        <p style={{ margin: 0 }}>{tr('CL-SCREENS-MAP-39')}{" "}{info.level} {" "}{tr('CL-SCREENS-MAP-40')}{" "}{me?.level ?? 1}{tr('CL-SCREENS-MAP-41')}{xpLeft !== null ? tr('CL-SCREENS-MAP-42', { p0: num(xpLeft) }) : ''}</p>
        <p class="small muted" style={{ margin: 0 }}>{tr('CL-SCREENS-MAP-43')}</p>
      </div>
    </Sheet>
  );
}
